import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BreadcrumbNav from '@/components/base/BreadcrumbNav';
import ScrollToTop from '@/components/base/ScrollToTop';
import { allMovies, categories } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = allMovies.find((m) => m.id === id);

  if (!movie) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-800 mb-2">
              内容未找到
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              抱歉，您要找的影视内容不存在或已下架
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === movie.categorySlug);

  const relatedMovies = allMovies
    .filter(
      (m) => m.categorySlug === movie.categorySlug && m.id !== movie.id
    )
    .slice(0, 6);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <article className="pt-16 md:pt-18">
        <BreadcrumbNav
          customPath={[
            { name: '首页', path: '/' },
            { name: movie.category, path: `/category/${movie.categorySlug}` },
            { name: movie.title },
          ]}
        />

        {/* Article Header */}
        <header className="py-8 md:py-12 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12">
              {/* Cover */}
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <img
                  src={movie.coverImage}
                  alt={movie.title}
                  className="w-48 md:w-56 lg:w-64 rounded-xl shadow-lg object-cover"
                  style={{ aspectRatio: '2/3' }}
                />
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-primary-100 text-primary-600 text-xs font-medium rounded-full whitespace-nowrap">
                    {movie.category}
                  </span>
                  {movie.isHot && (
                    <span className="px-3 py-1 bg-accent-100 text-accent-600 text-xs font-medium rounded-full whitespace-nowrap">
                      热门推荐
                    </span>
                  )}
                  {movie.isNew && (
                    <span className="px-3 py-1 bg-secondary-100 text-secondary-600 text-xs font-medium rounded-full whitespace-nowrap">
                      最新上线
                    </span>
                  )}
                </div>

                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-2">
                  {movie.title}
                </h1>
                <p className="text-sm md:text-base text-foreground-500 mb-4">
                  {movie.englishTitle}
                </p>

                <div className="flex flex-wrap items-center gap-4 mb-5 text-sm">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-400 text-lg"></i>
                    <strong className="text-xl text-foreground-900">{movie.rating}</strong>
                    <span className="text-foreground-500">/ 10</span>
                  </span>
                  <span className="text-foreground-400">|</span>
                  <span className="text-foreground-600">{movie.year}</span>
                  <span className="text-foreground-400">|</span>
                  <span className="text-foreground-600">{movie.region}</span>
                  <span className="text-foreground-400">|</span>
                  <span className="text-foreground-600">{movie.duration}</span>
                  {movie.totalEpisodes > 1 && (
                    <>
                      <span className="text-foreground-400">|</span>
                      <span className="text-foreground-600">共{movie.totalEpisodes}集</span>
                    </>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-5">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-background-100 border border-background-200/60 text-foreground-600 text-xs rounded-lg whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6 max-w-2xl">
                  {movie.description}
                </p>

                <div className="space-y-3 text-sm text-foreground-600 mb-6">
                  <p>
                    <span className="font-medium text-foreground-800">导演：</span>
                    {movie.director}
                  </p>
                  <p>
                    <span className="font-medium text-foreground-800">主演：</span>
                    {movie.actors.join(' / ')}
                  </p>
                  <p>
                    <span className="font-medium text-foreground-800">更新状态：</span>
                    <span className="text-primary-500 font-medium">{movie.updateEpisode}</span>
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-line text-lg"></i>
                    立即播放
                  </button>
                  <button className="inline-flex items-center gap-2 px-4 py-3 bg-background-100 hover:bg-background-200 border border-background-200/60 text-foreground-600 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer">
                    <i className="ri-heart-line text-lg"></i>
                    收藏
                  </button>
                  <button className="inline-flex items-center gap-2 px-4 py-3 bg-background-100 hover:bg-background-200 border border-background-200/60 text-foreground-600 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer">
                    <i className="ri-share-forward-line text-lg"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Related */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                相关推荐
              </h2>
              <Link
                to={`/category/${movie.categorySlug}`}
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 whitespace-nowrap"
              >
                更多{movie.category}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            {relatedMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                {relatedMovies.map((m) => (
                  <Link
                    key={m.id}
                    to={`/article/${m.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-xl mb-3">
                      <img
                        src={m.coverImage}
                        alt={m.title}
                        className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
                        style={{ aspectRatio: '2/3' }}
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                        <p className="text-xs text-background-50 line-clamp-2">
                          {m.description}
                        </p>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1 mb-1">
                      {m.title}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-400 text-xs"></i>
                        {m.rating}
                      </span>
                      <span>{m.year}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-center text-sm text-foreground-500 py-12">
                暂无相关推荐
              </p>
            )}
          </div>
        </section>
      </article>

      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Movie',
          name: movie.title,
          description: movie.description,
          image: movie.coverImage,
          dateCreated: `${movie.year}`,
          countryOfOrigin: movie.region,
          director: {
            '@type': 'Person',
            name: movie.director,
          },
          actor: movie.actors.map((actor) => ({
            '@type': 'Person',
            name: actor,
          })),
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: movie.rating,
            bestRating: 10,
          },
        })}
      </script>
    </>
  );
}